import { JOB_TYPE_LABELS } from '../enums/job-type.enum.js'
import type { ProblemSeverity } from '../enums/problem-severity.enum.js'
import type { OfferedProposalPlan } from '../enums/proposal-plan.enum.js'
import { UNIDENTIFIED_LABEL } from '../enums/tree-mark.enum.js'
import { TREE_WORK_CHOICE_PHRASES } from '../enums/tree-work-choice.enum.js'
import type {
  ProposalDocumentDto,
  TreeScheduleRow,
} from '../dto/proposal-document.dto.js'
import { formatMoneyValue, parseMoney } from './money.js'
import {
  EMPTY_SELECTION,
  UNIDENTIFIED_SELECTION_KEY,
  irrigationScheduleBlocks,
  priceSelection,
  type ExcludedRepair,
  type ExcludedTree,
  type ProposalSelection,
} from './proposal-selection.js'

/**
 * Plans: ready-made scopes a client picks before adjusting anything.
 *
 * The proposal bids everything the arborist found. Most boards then want one
 * of a few obvious cuts of it: the hazards now, or all of it now, with or
 * without the optional work. These compute those cuts from data the document
 * already carries (each tree's worst severity and its price) and express
 * every one of them as work left out, in the ordinary selection. So the
 * acceptance API prices a plan with the same helper the page used, from
 * choices rather than from a figure a browser sent. A board with a budget
 * adjusts what is included on top of a plan.
 */

/** Serious or Critical: a credible risk to people or property. */
export const HAZARD_SEVERITY = 4

/** Moderate: work for this season, though not a hazard. */
export const THIS_SEASON_SEVERITY = 3

/**
 * How urgent a tree is, from the worst problem recorded on it.
 *
 * The order is the order a plan's legend reads them. One list, so a page
 * that draws the legend cannot invent a fourth urgency or drop one.
 */
export const TREE_PRIORITIES = ['hazard', 'season', 'routine'] as const

export type TreePriority = (typeof TREE_PRIORITIES)[number]

export function treePriority(worst: ProblemSeverity | null): TreePriority {
  if (worst !== null && worst >= HAZARD_SEVERITY) return 'hazard'
  if (worst !== null && worst >= THIS_SEASON_SEVERITY) return 'season'
  return 'routine'
}

/** One tree as the plans see it. */
export interface PlanTree {
  site: string
  label: string
  priority: TreePriority
  /** Offered, but not part of the recommendation. */
  isOptional: boolean
}

function worstSeverityOf(row: TreeScheduleRow): ProblemSeverity | null {
  let worst: ProblemSeverity | null = null
  for (const problem of row.problems) {
    if (problem.severity === null) continue
    if (worst === null || problem.severity > worst) worst = problem.severity
  }
  return worst
}

/**
 * How urgent one schedule row is.
 *
 * The same rule the plans use. A page that re-derives it from `4` and `3`
 * would drift the day those thresholds move.
 */
export function scheduleRowPriority(row: TreeScheduleRow): TreePriority {
  return treePriority(worstSeverityOf(row))
}

/** Every tree in the proposal, in schedule order. */
export function planTrees(document: ProposalDocumentDto): PlanTree[] {
  const trees: PlanTree[] = []
  for (const block of document.blocks) {
    if (block.type !== 'treeSchedule') continue
    for (const row of block.rows) {
      trees.push({
        site: block.siteName,
        label: row.label,
        priority: treePriority(worstSeverityOf(row)),
        isOptional: row.isOptional,
      })
    }
  }
  return trees
}

/**
 * One irrigation repair as the plans see it. Repairs are never optional; the
 * arborist's "optional" is about trees.
 */
export interface PlanRepair {
  site: string
  key: string
  kind: string
  subject: string
  priority: TreePriority
}

/** Every irrigation repair in the proposal, site by site. */
export function planRepairs(document: ProposalDocumentDto): PlanRepair[] {
  const repairs: PlanRepair[] = []
  for (const block of irrigationScheduleBlocks(document)) {
    for (const repair of block.repairs) {
      repairs.push({
        site: block.siteName,
        key: repair.key,
        kind: repair.kind,
        subject: repair.subject,
        priority: treePriority(repair.severity),
      })
    }
  }
  return repairs
}

function leaveOut(
  trees: PlanTree[],
  repairs: PlanRepair[] = [],
): ProposalSelection {
  return {
    ...EMPTY_SELECTION,
    excludedTrees: trees.map((tree) => ({
      site: tree.site,
      label: tree.label,
    })),
    excludedRepairs: repairs.map((repair) => ({
      site: repair.site,
      key: repair.key,
    })),
  }
}

/**
 * The selection a plan starts from, before the client adjusts anything.
 * "Everything recommended" (CORE) leaves out what the arborist marked
 * optional.
 */
export function presetSelection(
  document: ProposalDocumentDto,
  plan: OfferedProposalPlan,
): ProposalSelection {
  switch (plan) {
    case 'ALL':
      return EMPTY_SELECTION
    case 'CORE':
      return leaveOut(planTrees(document).filter((tree) => tree.isOptional))
    case 'HAZARDS':
      return leaveOut(
        planTrees(document).filter((tree) => tree.priority !== 'hazard'),
        planRepairs(document).filter((repair) => repair.priority !== 'hazard'),
      )
  }
}

/** A plan's headline figures, for the cards a client chooses between. */
export interface PlanFigures {
  plan: OfferedProposalPlan
  /** Whether the plan means anything for this proposal. */
  isAvailable: boolean
  /** Trees in this plan's scope. */
  treeCount: number
  /** Irrigation repairs in this plan's scope. */
  repairCount: number
  /** The price of that scope, before tax, as an exact decimal string. */
  totalAmount: string | null
  /** What the client pays for it, tax included. */
  amountDue: string | null
}

/**
 * The offered plans and what each comes to.
 *
 * A plan that would be the same as another, or empty, is marked unavailable
 * rather than shown: "Hazards first" on a proposal with no hazards would be a
 * card offering nothing.
 */
export function planFigures(document: ProposalDocumentDto): PlanFigures[] {
  const trees = planTrees(document)
  const repairs = planRepairs(document)
  const items = trees.length + repairs.length
  const hazards =
    trees.filter((tree) => tree.priority === 'hazard').length +
    repairs.filter((repair) => repair.priority === 'hazard').length
  const optional = trees.filter((tree) => tree.isOptional).length
  const recommended = items - optional

  const figures = (plan: OfferedProposalPlan, isAvailable: boolean): PlanFigures => {
    const selection = presetSelection(document, plan)
    const outcome = priceSelection(document, selection)
    return {
      plan,
      isAvailable,
      treeCount: trees.length - selection.excludedTrees.length,
      repairCount: repairs.length - selection.excludedRepairs.length,
      totalAmount: outcome.totalAmount,
      amountDue: outcome.amountDue,
    }
  }

  // Counted across trees and repairs alike: a plan is about the work, and a
  // proposal of irrigation alone still has hazards.
  return [
    figures('HAZARDS', hazards > 0 && hazards < items),
    // "Everything recommended" splits from "everything" only when the
    // arborist marked some work optional and some not.
    figures('CORE', optional > 0 && recommended > 0),
    figures('ALL', items > 0),
  ]
}

/**
 * What the client changed on top of a plan.
 *
 * The ordinary selection fields, plus trees added back that the plan had left
 * out. Kept apart from the plan so each change can be listed in words, undone
 * on its own, and survive switching plans.
 */
export interface Adjustments extends ProposalSelection {
  includedTrees: ExcludedTree[]
  /** Repairs added back that the plan had left out. */
  includedRepairs: ExcludedRepair[]
}

export const NO_ADJUSTMENTS: Adjustments = {
  ...EMPTY_SELECTION,
  includedTrees: [],
  includedRepairs: [],
}

function sameTree(a: ExcludedTree, b: ExcludedTree): boolean {
  return a.site === b.site && a.label === b.label
}

function sameRepair(a: ExcludedRepair, b: ExcludedRepair): boolean {
  return a.site === b.site && a.key === b.key
}

/** The plan with the client's changes applied: what is actually priced. */
export function effectiveSelection(
  document: ProposalDocumentDto,
  plan: OfferedProposalPlan,
  adjustments: Adjustments,
): ProposalSelection {
  const preset = presetSelection(document, plan)
  const fromPlan = preset.excludedTrees.filter(
    (tree) => !adjustments.includedTrees.some((kept) => sameTree(kept, tree)),
  )
  const byHand = adjustments.excludedTrees.filter(
    (tree) => !fromPlan.some((planned) => sameTree(planned, tree)),
  )
  const repairsFromPlan = preset.excludedRepairs.filter(
    (repair) =>
      !adjustments.includedRepairs.some((kept) => sameRepair(kept, repair)),
  )
  const repairsByHand = adjustments.excludedRepairs.filter(
    (repair) => !repairsFromPlan.some((planned) => sameRepair(planned, repair)),
  )
  // Field by field, and the compiler holds it complete: a field missed here
  // would be a choice the client made that the price quietly ignores.
  return {
    excludedSites: adjustments.excludedSites,
    excludedSpecies: adjustments.excludedSpecies,
    excludedJobTypes: adjustments.excludedJobTypes,
    excludedProblems: adjustments.excludedProblems,
    treeChoices: adjustments.treeChoices,
    excludedTrees: [...fromPlan, ...byHand],
    excludedAreas: adjustments.excludedAreas,
    excludedAreaNames: adjustments.excludedAreaNames,
    excludedIrrigation: adjustments.excludedIrrigation,
    excludedIrrigationKinds: adjustments.excludedIrrigationKinds,
    excludedZones: adjustments.excludedZones,
    excludedRepairs: [...repairsFromPlan, ...repairsByHand],
  }
}

/** Which list a change lives in, and where. */
export type AdjustmentField = keyof Adjustments

/**
 * One adjustment field replaced outright.
 *
 * The controls write a single list or flag at a time. A `Partial` would also
 * allow an empty patch, or several fields that do not belong together, and
 * under exact optional properties a missing field is not the same as a value.
 */
export type AdjustmentPatch = {
  [K in keyof Adjustments]: { readonly field: K; readonly value: Adjustments[K] }
}[keyof Adjustments]

/** The adjustments with one field replaced. */
export function applyAdjustment(
  current: Adjustments,
  patch: AdjustmentPatch,
): Adjustments {
  switch (patch.field) {
    case 'excludedSites':
      return { ...current, excludedSites: patch.value }
    case 'excludedSpecies':
      return { ...current, excludedSpecies: patch.value }
    case 'excludedJobTypes':
      return { ...current, excludedJobTypes: patch.value }
    case 'excludedProblems':
      return { ...current, excludedProblems: patch.value }
    case 'treeChoices':
      return { ...current, treeChoices: patch.value }
    case 'excludedTrees':
      return { ...current, excludedTrees: patch.value }
    case 'excludedAreas':
      return { ...current, excludedAreas: patch.value }
    case 'excludedAreaNames':
      return { ...current, excludedAreaNames: patch.value }
    case 'includedTrees':
      return { ...current, includedTrees: patch.value }
    case 'excludedIrrigation':
      return { ...current, excludedIrrigation: patch.value }
    case 'excludedIrrigationKinds':
      return { ...current, excludedIrrigationKinds: patch.value }
    case 'excludedZones':
      return { ...current, excludedZones: patch.value }
    case 'excludedRepairs':
      return { ...current, excludedRepairs: patch.value }
    case 'includedRepairs':
      return { ...current, includedRepairs: patch.value }
    default: {
      const unhandled: never = patch
      return unhandled
    }
  }
}

/** One change from the plan, said in words, with what it is worth. */
export interface AdjustmentChange {
  field: AdjustmentField
  index: number
  text: string
  /**
   * What this change does to the price before tax, in cents: negative when
   * it takes work out. Measured against all the other changes, so changes
   * that overlap (an area and a tree inside it) do not add up to the total.
   */
  deltaCents: number
}

/** The adjustments with one change removed, for an undo. */
export function withoutChange(
  adjustments: Adjustments,
  change: Pick<AdjustmentChange, 'field' | 'index'>,
): Adjustments {
  const next: Adjustments = { ...adjustments }
  switch (change.field) {
    case 'excludedSites':
      next.excludedSites = adjustments.excludedSites.filter((_, i) => i !== change.index)
      break
    case 'excludedSpecies':
      next.excludedSpecies = adjustments.excludedSpecies.filter((_, i) => i !== change.index)
      break
    case 'excludedJobTypes':
      next.excludedJobTypes = adjustments.excludedJobTypes.filter((_, i) => i !== change.index)
      break
    case 'excludedProblems':
      next.excludedProblems = adjustments.excludedProblems.filter((_, i) => i !== change.index)
      break
    case 'treeChoices':
      next.treeChoices = adjustments.treeChoices.filter((_, i) => i !== change.index)
      break
    case 'excludedTrees':
      next.excludedTrees = adjustments.excludedTrees.filter((_, i) => i !== change.index)
      break
    case 'excludedAreas':
      next.excludedAreas = adjustments.excludedAreas.filter((_, i) => i !== change.index)
      break
    case 'excludedAreaNames':
      next.excludedAreaNames = adjustments.excludedAreaNames.filter((_, i) => i !== change.index)
      break
    case 'includedTrees':
      next.includedTrees = adjustments.includedTrees.filter((_, i) => i !== change.index)
      break
    case 'excludedIrrigation':
      next.excludedIrrigation = false
      break
    case 'excludedIrrigationKinds':
      next.excludedIrrigationKinds = adjustments.excludedIrrigationKinds.filter((_, i) => i !== change.index)
      break
    case 'excludedZones':
      next.excludedZones = adjustments.excludedZones.filter((_, i) => i !== change.index)
      break
    case 'excludedRepairs':
      next.excludedRepairs = adjustments.excludedRepairs.filter((_, i) => i !== change.index)
      break
    case 'includedRepairs':
      next.includedRepairs = adjustments.includedRepairs.filter((_, i) => i !== change.index)
      break
    default: {
      // Every field is a case above: a new one must say how it is undone.
      const unhandled: never = change.field
      return unhandled
    }
  }
  return next
}

function areaName(
  document: ProposalDocumentDto,
  site: string,
  areaId: string,
): string {
  for (const block of document.blocks) {
    if (block.type !== 'siteMap' || block.siteName !== site) continue
    const area = (block.areas ?? []).find((entry) => entry.id === areaId)
    if (area !== undefined) return area.name
  }
  return 'An area'
}

/**
 * Every change from the plan, in the order a person would read them: areas,
 * then kinds of work and problems, then single trees.
 *
 * The text names trees by label only when the proposal covers one property,
 * and adds the property when it covers several, because "Tree 12" is
 * ambiguous across two parks.
 */
export function describeAdjustments(
  document: ProposalDocumentDto,
  plan: OfferedProposalPlan,
  adjustments: Adjustments,
): AdjustmentChange[] {
  const all = planTrees(document)
  const sites = new Set(all.map((tree) => tree.site))
  const optionalTrees = new Set(
    all
      .filter((tree) => tree.isOptional)
      .map((tree) => `${tree.site}\u0000${tree.label}`),
  )
  const treeName = (tree: ExcludedTree): string =>
    sites.size > 1 ? `Tree ${tree.label} (${tree.site})` : `Tree ${tree.label}`
  const repairs = planRepairs(document)
  const repairSites = new Set(repairs.map((repair) => repair.site))
  const atSite = (text: string, site: string): string =>
    repairSites.size > 1 ? `${text} (${site})` : text
  const repairName = (excluded: ExcludedRepair): string => {
    const repair = repairs.find((entry) => sameRepair(entry, excluded))
    return repair === undefined
      ? 'A repair'
      : atSite(`${repair.kind} on ${repair.subject}`, repair.site)
  }

  const entries: Omit<AdjustmentChange, 'deltaCents'>[] = [
    ...adjustments.excludedSites.map((site, index) => ({
      field: 'excludedSites' as const,
      index,
      text: `${site} left out`,
    })),
    ...adjustments.excludedAreas.map((area, index) => ({
      field: 'excludedAreas' as const,
      index,
      text: `${areaName(document, area.site, area.areaId)} left out`,
    })),
    ...adjustments.excludedAreaNames.map((area, index) => ({
      field: 'excludedAreaNames' as const,
      index,
      text: `${area.name} left out`,
    })),
    ...adjustments.excludedJobTypes.map((type, index) => ({
      field: 'excludedJobTypes' as const,
      index,
      text: `${JOB_TYPE_LABELS[type]} left out`,
    })),
    ...adjustments.excludedProblems.map((name, index) => ({
      field: 'excludedProblems' as const,
      index,
      text: `Treatment left out: ${name}`,
    })),
    ...adjustments.excludedSpecies.map((key, index) => ({
      field: 'excludedSpecies' as const,
      index,
      text: `${key === UNIDENTIFIED_SELECTION_KEY ? UNIDENTIFIED_LABEL : key} left out`,
    })),
    ...adjustments.excludedTrees.map((tree, index) => ({
      field: 'excludedTrees' as const,
      index,
      text: `${treeName(tree)} left out`,
    })),
    ...adjustments.includedTrees.map((tree, index) => ({
      field: 'includedTrees' as const,
      index,
      text: optionalTrees.has(`${tree.site}\u0000${tree.label}`)
        ? `${treeName(tree)} added (optional work)`
        : `${treeName(tree)} added back`,
    })),
    ...adjustments.treeChoices.map((choice, index) => ({
      field: 'treeChoices' as const,
      index,
      text: `${treeName(choice)} ${TREE_WORK_CHOICE_PHRASES[choice.choice]}`,
    })),
    ...(adjustments.excludedIrrigation
      ? [
          {
            field: 'excludedIrrigation' as const,
            index: 0,
            text: 'Irrigation repairs left out',
          },
        ]
      : []),
    ...adjustments.excludedIrrigationKinds.map((kind, index) => ({
      field: 'excludedIrrigationKinds' as const,
      index,
      text: `Repairs left out: ${kind}`,
    })),
    ...adjustments.excludedZones.map((zone, index) => ({
      field: 'excludedZones' as const,
      index,
      text: `${atSite(zone.zone, zone.site)} left out`,
    })),
    ...adjustments.excludedRepairs.map((repair, index) => ({
      field: 'excludedRepairs' as const,
      index,
      text: `${repairName(repair)} left out`,
    })),
    ...adjustments.includedRepairs.map((repair, index) => ({
      field: 'includedRepairs' as const,
      index,
      text: `${repairName(repair)} added back`,
    })),
  ]

  const totalCents = (next: Adjustments): number =>
    parseMoney(
      priceSelection(
        document,
        effectiveSelection(document, plan, next),
      ).totalAmount,
    ) ?? 0

  const current = totalCents(adjustments)
  return entries.map((entry) => ({
    ...entry,
    deltaCents: current - totalCents(withoutChange(adjustments, entry)),
  }))
}

/**
 * What a selection leaves out, split by whether we recommended it, before tax.
 *
 * The recommended part is work the property needs, left for later; the
 * optional part is work we offered and never said it needed, so it is only
 * not included. A page saying "$850 of the recommended work is left for later"
 * to a board that took "Everything recommended" would contradict the plan it
 * just chose. Each is an exact decimal string, or null when nothing of it is
 * left out; together they make the selection's `deferredAmount`.
 */
export function leftOutWork(
  document: ProposalDocumentDto,
  selection: ProposalSelection,
): { recommended: string | null; optional: string | null } {
  const optionalTrees = planTrees(document)
    .filter((tree) => tree.isOptional)
    .map((tree) => ({ site: tree.site, label: tree.label }))
  const cents = (candidate: ProposalSelection): number =>
    parseMoney(priceSelection(document, candidate).totalAmount) ?? 0
  const recommendedOf = (candidate: ProposalSelection): number =>
    cents({
      ...candidate,
      excludedTrees: [...candidate.excludedTrees, ...optionalTrees],
    })

  const offered = cents(EMPTY_SELECTION)
  const offeredRecommended = recommendedOf(EMPTY_SELECTION)
  const chosen = cents(selection)
  const chosenRecommended = recommendedOf(selection)
  const recommended = offeredRecommended - chosenRecommended
  const optional = offered - offeredRecommended - (chosen - chosenRecommended)
  return {
    recommended: recommended > 0 ? formatMoneyValue(recommended) : null,
    optional: optional > 0 ? formatMoneyValue(optional) : null,
  }
}

/**
 * The plan a proposal opens on: the recommendation. When the arborist marked
 * work optional that is CORE, so the first figure a board sees is the one we
 * recommend, with the optional work one tap away; otherwise it is everything.
 */
export function recommendedPlan(document: ProposalDocumentDto): OfferedProposalPlan {
  const core = planFigures(document).find((figure) => figure.plan === 'CORE')
  return core?.isAvailable === true ? 'CORE' : 'ALL'
}
